import {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import Icon from "commons/components/AppIcon";

/* ============================================
   ORIGINAL VERSION (COMMENTED OUT)
   ============================================
   
import { createContext, useCallback, useContext, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "commons/components/AppIcon";

const SnackbarContext = createContext();

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    time: 5000,
    status: "",
  });
  
  const showSnackbar = useCallback(
    ({ message, time = 5000, status = "info" }) => {
      setSnackbar({ open: true, message, time, status });

      setTimeout(() => {
        setSnackbar((prev) => ({ ...prev, open: false }));
      }, time);
    },
    []
  );
  
  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      {snackbar.open && createPortal(
        <div
          className={`fixed text-white bottom-[60px] right-10 w-[320px] rounded-sm shadow-lg flex p-4 z-[9999] gap-4 ${
            snackbar.status === "success" ? "bg-success-600" : "bg-error-600"
          }`}
          style={{ zIndex: 9999 }}
        >
          <div className="flex items-center gap-4 flex-1">
            <div className="">
              <Icon size={32} name={"MessageCircleWarning"} />
            </div>
            <p className="text">{snackbar.message}</p>
          </div>
          <button
            className="flex-0"
            onClick={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          >
            <Icon size={16} name={"X"} />
          </button>
        </div>,
        document.body
      )}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => useContext(SnackbarContext);

   ============================================
   END OF ORIGINAL VERSION
   ============================================ */

const SnackbarContext = createContext();

// Progress Bar Component
const ProgressBar = ({ time, status }) => {
  return (
    <div className="absolute bottom-0 left-0 right-0 h-1 bg-border/20 rounded-b-2xl overflow-hidden">
      <div
        className={`h-full ${
          status === "success" ? "bg-success-600" : "bg-error-600"
        }`}
        style={{
          animation: `shrink ${time}ms linear forwards`,
        }}
      />
      <style>{`
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
};

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    time: 5000,
    status: "",
  });

  // Track if snackbar is fading out
  const [isClosing, setIsClosing] = useState(false);

  // Key to force re-render of progress bar
  const [progressKey, setProgressKey] = useState(0);

  // Detect if device is mobile or tablet
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      // Check viewport width and touch capability
      const isMobile = window.innerWidth < 768; // Mobile breakpoint
      const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024; // Tablet breakpoint
      const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

      setIsMobileOrTablet((isMobile || isTablet) && hasTouch);
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);

    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  const showSnackbar = useCallback(
    ({ message, time = 5000, status = "info" }) => {
      setSnackbar({ open: true, message, time, status });
      setIsClosing(false);
      setProgressKey((prev) => prev + 1); // Reset progress bar animation

      // Start fade-out 300ms before hiding
      const fadeTimer = setTimeout(() => {
        setIsClosing(true);
      }, time - 300);

      // Hide completely after fade-out
      const hideTimer = setTimeout(() => {
        setSnackbar((prev) => ({ ...prev, open: false }));
        setIsClosing(false);
      }, time);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    },
    [],
  );

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setSnackbar((prev) => ({ ...prev, open: false }));
      setIsClosing(false);
    }, 300); // Match transition duration
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      {snackbar.open &&
        createPortal(
          <div
            className={`fixed rounded-2xl shadow-2xl flex items-start p-4 z-[9999] gap-3 transition-all duration-300 border bg-white overflow-hidden ${
              snackbar.status === "success"
                ? "border-green-200"
                : "border-red-200"
            } ${
              isMobileOrTablet
                ? "top-6 right-6 left-6 max-w-[calc(100vw-48px)] sm:left-auto sm:max-w-[400px]"
                : "top-6 right-6 w-[400px] max-w-[calc(100vw-48px)]"
            } ${
              isClosing
                ? "opacity-0 translate-y-2 scale-95"
                : "opacity-100 translate-y-0 scale-100 animate-slide-in"
            }`}
            style={{ zIndex: 9999 }}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {/* Icon with colored background */}
            <div
              className={`mt-0.5 flex-shrink-0 rounded-full p-2 ${
                snackbar.status === "success"
                  ? "bg-success-100 text-success-600"
                  : "bg-error-100 text-error-600"
              }`}
            >
              <Icon
                size={18}
                name={
                  snackbar.status === "success" ? "CheckCircle2" : "AlertCircle"
                }
              />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-text-primary capitalize">
                {snackbar.status}
              </p>
              <p className="mt-0.5 text-sm text-text-secondary leading-relaxed break-words">
                {snackbar.message}
              </p>
            </div>

            {/* Close button */}
            <button
              className="ml-1 flex-shrink-0 rounded-md p-1.5 text-text-secondary/80 hover:text-text-primary hover:bg-muted/50 transition-colors"
              onClick={handleClose}
              aria-label="Close"
              type="button"
            >
              <Icon size={16} name={"X"} />
            </button>

            {/* Progress Bar */}
            <ProgressBar
              key={progressKey}
              time={snackbar.time}
              status={snackbar.status}
            />
          </div>,
          document.body,
        )}
    </SnackbarContext.Provider>
  );
};
export const useSnackbar = () => useContext(SnackbarContext);
